/**
 * ppocr.js — main-thread facade for the PP-OCRv5 engine.
 *
 * Owns model fetching (with Cache API persistence), the worker lifecycle and
 * the script→model routing. The heavy work lives in ppocr-worker.js.
 *
 * Measured against the Tesseract.js v5 build production serves today, on a
 * ground-truth corpus of 10 images / 3 scripts (tests/ocr):
 *
 *              character error rate     mean     A4 2480×3508
 *   Tesseract           11.0%           338 ms      1737 ms
 *   PP-OCRv5 wasm        1.5%           288 ms       636 ms
 *   PP-OCRv5 WebGPU      1.5%           131 ms       211 ms
 *
 * Tesseract returned an EMPTY string on a white-on-red Chinese sign; PP-OCRv5
 * read it exactly. WebGPU showed no accuracy regression against wasm — checked
 * rather than assumed, because a silently-degrading GPU path has bitten this
 * codebase before.
 *
 *   const eng = await PPOCR.open('ru', { onProgress });
 *   const res = await eng.read(imageBitmap, { onLine });
 */
(function (root) {
    'use strict';
    if (root.PPOCR) return;

    /**
     * Weights live under /vendor/onnx/ (downloaded at deploy time, gitignored);
     * the worker is ordinary versioned app code under /js. Two reasons for the
     * split: static /vendor is served immutable for up to a year, so a worker
     * parked there would never pick up a fix (callers MUST pass `workerUrl`
     * from the asset router), and the shared model cache only recognises
     * /vendor/onnx|rife|omr — a path outside it silently loses caching.
     */
    var BASE = '/vendor/onnx/ocr-neural/';
    var DEFAULT_WORKER = '/js/ocr-neural/ppocr-worker.js';
    /** Owned by the shared model-cache-put block, not by this file. */
    var CACHE = 'onnx-models';

    /**
     * One detection model serves every script; only the recogniser changes.
     * That keeps the second language a 7.5 MB download instead of 12.
     */
    var DET = { url: BASE + 'det-mobile.onnx', mb: 4.5 };
    var SCRIPTS = {
        latin: { rec: 'rec-latin.onnx', dict: 'dict-latin.txt', mb: 7.5 },
        eslav: { rec: 'rec-eslav.onnx', dict: 'dict-eslav.txt', mb: 7.5 },
        ch: { rec: 'rec-ch.onnx', dict: 'dict-ch.txt', mb: 15.8 },
        korean: { rec: 'rec-korean.onnx', dict: 'dict-korean.txt', mb: 12.8 },
        arabic: { rec: 'rec-arabic.onnx', dict: 'dict-arabic.txt', mb: 8.6 },
        greek: { rec: 'rec-greek.onnx', dict: 'dict-greek.txt', mb: 7.4 },
        devanagari: { rec: 'rec-hindi.onnx', dict: 'dict-hindi.txt', mb: 8.6 },
        thai: { rec: 'rec-thai.onnx', dict: 'dict-thai.txt', mb: 7.5 },
    };

    /** BCP-47 language → recognition script. */
    var LANG_SCRIPT = {
        ru: 'eslav', uk: 'eslav', be: 'eslav', bg: 'eslav', sr: 'eslav', mk: 'eslav',
        zh: 'ch', 'zh-CN': 'ch', 'zh-TW': 'ch', ja: 'ch',
        ko: 'korean',
        ar: 'arabic', fa: 'arabic', ur: 'arabic',
        el: 'greek', hi: 'devanagari', th: 'thai',
    };
    /** Everything else that is written in the Latin alphabet. */
    function scriptFor(lang) {
        if (!lang) return 'latin';
        var base = String(lang).split('-')[0];
        return LANG_SCRIPT[lang] || LANG_SCRIPT[base] || 'latin';
    }

    // ── homoglyph folding ────────────────────────────────────────────────

    var TO_CYRILLIC = {
        A: 'А', B: 'В', C: 'С', E: 'Е', G: 'Г', H: 'Н', K: 'К', L: 'Л', M: 'М',
        N: 'И', O: 'О', P: 'Р', R: 'Я', T: 'Т', U: 'И', X: 'Х', Y: 'У', '3': 'З',
        a: 'а', c: 'с', e: 'е', h: 'н', k: 'к', m: 'м', o: 'о', p: 'р',
        r: 'г', u: 'и', x: 'х', y: 'у',
        'Α': 'А', 'Β': 'В', 'Γ': 'Г', 'Ε': 'Е', 'Ζ': 'З', 'Η': 'Н', 'Ι': 'І',
        'Κ': 'К', 'Λ': 'Л', 'Μ': 'М', 'Ν': 'Н', 'Ο': 'О', 'Π': 'П', 'Ρ': 'Р',
        'Σ': 'С', 'Τ': 'Т', 'Υ': 'У', 'Φ': 'Ф', 'Χ': 'Х',
        'α': 'а', 'ε': 'е', 'ι': 'і', 'ο': 'о', 'ρ': 'р', 'υ': 'у', 'χ': 'х',
    };
    var CYR = /[Ѐ-ӿ]/;
    var NON_CYR_LETTER = /[A-Za-zͰ-Ͽ]/;

    /**
     * A real word never mixes Cyrillic with Latin or Greek, so a token that does
     * is by definition an OCR artefact and folding it is safe. Purely Latin
     * tokens ("Wi-Fi", "E150", "Espresso") are left alone.
     *
     * Measured on the sign case: "АΠТЕКА … LeHиHa" at 16.2% CER became exact.
     * A majority-script rule got this wrong — it refused "LeHиHa" because only
     * one letter of six was Cyrillic, and left the word looking foreign to the
     * translator downstream.
     */
    function foldCyrillic(text) {
        return String(text).split(/(\s+)/).map(function (tok) {
            if (/^\s*$/.test(tok)) return tok;
            if (!CYR.test(tok) || !NON_CYR_LETTER.test(tok)) return tok;

            // The recogniser reports several lowercase Cyrillic letters in their
            // uppercase Latin lookalike ("н" comes back as "H"), so a straight
            // map produces "ЛеНиНа" — right letters, wrong case. Follow the case
            // the rest of the token is already written in: a token whose Cyrillic
            // letters are lowercase gets lowercase substitutions.
            var upper = 0, lower = 0;
            Array.from(tok).forEach(function (ch) {
                if (!CYR.test(ch)) return;
                if (ch === ch.toUpperCase() && ch !== ch.toLowerCase()) upper++;
                else if (ch === ch.toLowerCase() && ch !== ch.toUpperCase()) lower++;
            });
            var preferLower = lower > upper;

            return Array.from(tok).map(function (ch) {
                var m = TO_CYRILLIC[ch];
                if (!m) return ch;
                return preferLower ? m.toLowerCase() : m;
            }).join('');
        }).join('');
    }

    // ── right-to-left reading order ────────────────────────────────────────────────

    // Characters that keep their own left-to-right order inside Arabic text:
    // Latin letters, both digit sets and the punctuation that lives in numbers.
    var LTR_CHAR = /[A-Za-z0-9\u0660-\u0669\u06F0-\u06F9:*.\/%+\-]/;

    /**
     * The Arabic recogniser emits characters in VISUAL order — left to right, as
     * they sit on the image — which is the reverse of how Arabic is written.
     * PaddleOCR's own decoder reverses it (BaseRecLabelDecode.pred_reverse, on
     * for the arabic dictionary); ours did not, so مخرج came back as جرخم and the
     * translator was handed reversed words (measured 16.09.2026: 66.7 % CER on a
     * two-line exit sign, the characters all correct and all backwards).
     *
     * Runs of LTR characters keep their own order, as in Paddle, with one
     * difference: spaces at the EDGE of a run are their own tokens. Paddle glues
     * them to the run, which turns "مخرج 12" into "مخرج12 ".
     */
    function logicalRtl(text) {
        var parts = [], run = '';
        function flush() {
            if (!run) return;
            var lead = run.match(/^ */)[0], core = run.trim(), trail = run.length > lead.length ? run.match(/ *$/)[0] : '';
            if (lead) parts.push(lead);
            if (core) parts.push(core);
            if (trail && core) parts.push(trail);
            run = '';
        }
        Array.from(String(text == null ? '' : text)).forEach(function (c) {
            if (LTR_CHAR.test(c) || (c === ' ' && run)) { run += c; return; }
            flush();
            parts.push(c);
        });
        flush();
        return parts.reverse().join('');
    }

    /** The text as it is WRITTEN in this script: homoglyphs folded, RTL put in reading order. */
    function asWritten(script, text) {
        if (script === 'eslav') return foldCyrillic(text);
        if (script === 'arabic') return logicalRtl(text);
        return text;
    }
    function wordsAsWritten(script, words) {
        if (!words) return null;
        if (script !== 'arabic') return words;
        // Geometry (f0/f1) stays where the word is on the image; only the order of
        // the list and each word's own letters follow the reading direction.
        return words.slice().reverse().map(function (w) {
            var c = {};
            for (var k in w) if (Object.prototype.hasOwnProperty.call(w, k)) c[k] = w[k];
            c.text = logicalRtl(w.text);
            return c;
        });
    }

    // ── model fetch with persistent cache ────────────────────────────────

    /**
     * The shared resumable downloader, loaded on demand.
     *
     * A hand-rolled streaming fetch is NOT good enough here and this file had
     * one. Measured on prod over 30 days: 173 sessions across 8 tools died on a
     * model download, and the breaks land at random offsets (0, 33 KB, 27 MB of
     * 214 MB) — that is the channel, not the code. `__mdFetchBytes` carries the
     * seven lessons those failures paid for: Range resume, a silence watchdog
     * that aborts the fetch rather than cancelling the reader, treating a short
     * clean close as a break, `Content-Range` for the real total, and so on.
     * ModelDownloadResumeTest refuses a new streaming loader without it.
     */
    function loadDownloader() {
        if (root.__mdFetchBytes) return Promise.resolve(root.__mdFetchBytes);
        return new Promise(function (resolve, reject) {
            var s = document.createElement('script');
            s.src = '/js/model-download.js';
            s.onload = function () {
                root.__mdFetchBytes
                    ? resolve(root.__mdFetchBytes)
                    : reject(new Error('model-download loaded but registered nothing'));
            };
            s.onerror = function () { reject(new Error('model-download failed to load')); };
            document.head.appendChild(s);
        });
    }

    async function cachedFetch(url, onProgress) {
        // Read the shared cache FIRST. Writing to it without reading from it is
        // a cache that costs storage and saves nothing — and the page would go
        // on promising "downloaded once" while re-fetching every visit.
        try {
            var hit = await (await caches.open(CACHE)).match(url);
            if (hit) {
                if (onProgress) onProgress(1);
                return hit.arrayBuffer();
            }
        } catch (_) { /* no CacheStorage in this context */ }

        var md = await loadDownloader();
        var bytes = await md(url, {
            onProgress: function (received, total) {
                if (onProgress && total) onProgress(received / total);
            },
        });
        // The worker wants a transferable ArrayBuffer; slice so a view with an
        // offset cannot hand over more than its own bytes.
        var buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);

        /**
         * Put the weights in the shared model cache ourselves.
         *
         * `model-download.js` ships WITHOUT the model-cache-put block on purpose,
         * so its own caching is off, and the Service Worker's CacheFirst route
         * only starts working once the SW controls the page — from the second
         * navigation. Between those two facts a returning visitor could still
         * pay for the download, and `isCached()` — which decides whether the
         * page announces "12–20 MB" — would keep saying "not cached" while the
         * tool worked perfectly. One explicit put makes both honest.
         */
        try {
            var cache = await caches.open(CACHE);
            if (!(await cache.match(url))) {
                await cache.put(url, new Response(buf.slice(0), {
                    headers: { 'Content-Type': 'application/octet-stream' },
                }));
            }
        } catch (_) { /* private mode, quota, no CacheStorage — not fatal */ }

        return buf;
    }

    /** Is a script already on disk? Lets the UI say "ready" vs "12 MB to fetch". */
    /** `alsoLang` — the second recogniser of a dual engine (see open()). */
    function secondScript(lang, alsoLang) {
        if (!alsoLang) return null;
        var s2 = scriptFor(alsoLang);
        return s2 === scriptFor(lang) ? null : s2;
    }

    async function isCached(lang, alsoLang) {
        var s = SCRIPTS[scriptFor(lang)];
        if (!s) return false;
        var script2 = secondScript(lang, alsoLang);
        try {
            var cache = await caches.open(CACHE);
            var a = await cache.match(DET.url);
            var b = await cache.match(BASE + s.rec);
            var c = script2 && SCRIPTS[script2] ? await cache.match(BASE + SCRIPTS[script2].rec) : true;
            return !!(a && b && c);
        } catch (_) { return false; }
    }

    function downloadMb(lang, detCached, alsoLang) {
        var s = SCRIPTS[scriptFor(lang)];
        var script2 = secondScript(lang, alsoLang);
        var s2 = script2 ? SCRIPTS[script2] : null;
        return Math.round(((detCached ? 0 : DET.mb) + (s ? s.mb : 7.5) + (s2 ? s2.mb : 0)) * 10) / 10;
    }

    /**
     * WebGPU counts as available only when a DEVICE can actually be created.
     *
     * `requestAdapter()` succeeding proves nothing: measured on a Windows box
     * mid-session, the adapter resolved fine while `requestDevice()` threw
     * "DynamicLib.Open: dxil.dll Windows Error: 87". Reporting that as WebGPU
     * makes the worker try the GPU provider first, and ORT then memoises the
     * failed init so the wasm FALLBACK fails too, with the useless echo
     * "previous call to 'initWasm()' failed". One optimistic check turns a
     * degraded machine into a broken tool.
     */
    async function hasWebGpu() {
        if (!root.navigator || !navigator.gpu) return false;
        try {
            var adapter = await navigator.gpu.requestAdapter();
            if (!adapter) return false;
            var device = await adapter.requestDevice();
            if (!device) return false;
            try { device.destroy(); } catch (_) { }
            return true;
        } catch (_) { return false; }
    }

    function ae(type, data) { try { if (root.__ae) root.__ae(type, data); } catch (_) { } }
    function absUrl(p) { return /^https?:/i.test(String(p)) ? p : root.location.origin + p; }

    /**
     * Which ONNX Runtime build, and whether a GPU provider may be asked for —
     * the owner's answer (window.__ortBuild, device-capabilities.js), never this
     * file's own. Until 14.10.2026 the engine took the JSEP bundle and the WebGPU
     * provider wherever a GPU device could be created, which includes every
     * iPhone and iPad on iOS 18–26 — where the owner says plain wasm. Field, RU,
     * /ocr-text-extractor 12.09–13.10.2026: iOS WITH WebGPU — a result in roughly
     * one run of three, the tab dying silently right after «Recognise» in most of
     * the rest; iOS without WebGPU, same bundle on the wasm provider — 28 of 33
     * (comment #1812, an iPad: two dead tabs and a 112-s hang on one PNG).
     * `opts.ortUrl` stays for benches. Paths go to the worker ABSOLUTE: a
     * relative `wasmPaths` in a classic worker sends ORT's dynamic import of its
     * .mjs where it cannot be fetched («Failed to fetch dynamically imported
     * module» for a file the server answers 200 for).
     */
    function buildOf(opts) {
        var origin = root.location.origin, owner = root.__ortBuild;
        if (opts.ortUrl) return { url: opts.ortUrl, wasmBase: opts.wasmBase || origin + '/vendor/onnxruntime/', threads: 0, webgpu: true, legacy: '' };
        if (owner && owner.pick) {
            var p = owner.pick();
            return { url: absUrl(p.url), wasmBase: absUrl(p.wasmBase), threads: p.threads || 0, webgpu: !!p.webgpu, legacy: p.legacy || '' };
        }
        // No owner on the page (its script did not load): the build every engine runs.
        return { url: origin + '/vendor/onnxruntime/ort.wasm.min.js', wasmBase: origin + '/vendor/onnxruntime/', threads: 0, webgpu: false, legacy: '' };
    }

    /**
     * The verifier on the PAGE (the worker has its own copy): it remembers a GPU
     * that read the known answer wrong for 30 days and names the adapter in
     * telemetry. Its versioned URL rides in the worker's (`?d=…`, ppocr_worker_url()),
     * so no page has to load it by hand.
     */
    var _verifier = null;
    function depUrl(workerUrl, name) {
        try {
            var d = new URL(workerUrl, root.location.href).searchParams.get('d');
            var list = d ? d.split('|') : [];
            for (var i = 0; i < list.length; i++) if (list[i].split('?')[0].split('/').pop() === name) return list[i];
        } catch (_) { }
        return null;
    }
    function verifier(workerUrl) {
        if (root.OrtEpVerify) return Promise.resolve(root.OrtEpVerify);
        if (_verifier) return _verifier;
        _verifier = new Promise(function (resolve) {
            var s = document.createElement('script');
            s.src = depUrl(workerUrl, 'ort-ep-verify.js') || '/js/ort-ep-verify.js';
            s.onload = function () { resolve(root.OrtEpVerify || null); };
            s.onerror = function () { resolve(null); };
            document.head.appendChild(s);
        });
        return _verifier;
    }
    /** A GPU that hung or crashed is off for this TAB (a bad day is not a verdict). */
    var GPU_OFF_KEY = 'ppocr_gpu_off';
    function gpuOffHere() { try { return !!sessionStorage.getItem(GPU_OFF_KEY); } catch (_) { return false; } }
    function gpuOffForTab(why) { try { sessionStorage.setItem(GPU_OFF_KEY, String(why)); } catch (_) { } }

    function visibleTimeout(fn, ms) {
        if (typeof root.__visibleTimeout === 'function') return root.__visibleTimeout(fn, ms);
        var t = setTimeout(fn, ms); return function () { clearTimeout(t); };
    }
    /**
     * How long a run may stay SILENT before it is called stuck — counted in
     * visible time (a hidden tab is throttled, not stuck). The worker speaks
     * after detection ('boxes') and after every line ('line' / 'tick'); the
     * first message on a GPU also pays shader compilation for a new image size
     * (measured 3–6 s on a desktop GPU for the first read). Only a GPU run has
     * somewhere to fall back to; on the CPU the ceiling just turns a dead worker
     * into an error the page can show instead of an endless «Recognising…».
     */
    var GPU_FIRST_MS = 45000, GPU_GAP_MS = 30000, CPU_GAP_MS = 300000, INIT_MS = 60000;

    // ── engine ───────────────────────────────────────────────────────────

    async function open(lang, opts) {
        opts = opts || {};
        var script = scriptFor(lang);
        var s = SCRIPTS[script];
        if (!s) throw new Error('unsupported script for ' + lang);

        /**
         * `opts.alsoLang` makes a DUAL engine: one detector, two recognisers,
         * and every line comes back with `alt` — the second recogniser's
         * reading of the same crop. "Detect automatically" in /photo-translate
         * needs it: the script is unknown before reading, a Latin recogniser on
         * Cyrillic returns almost nothing, and a Cyrillic one loses the
         * diacritics its dictionary lacks — so the page picks per line
         * (photo-translate/script-pick.js).
         */
        var script2 = secondScript(lang, opts.alsoLang);
        var s2 = script2 ? SCRIPTS[script2] : null;
        if (script2 && !s2) throw new Error('unsupported script for ' + opts.alsoLang);

        var onProgress = opts.onProgress || function () { };
        var recShare = s2 ? 0.3 : 0.6;
        var detBytes = await cachedFetch(DET.url, function (f) { onProgress(f * 0.35); });
        var recBytes = await cachedFetch(BASE + s.rec, function (f) { onProgress(0.35 + f * recShare); });
        var rec2Bytes = s2 ? await cachedFetch(BASE + s2.rec, function (f) { onProgress(0.65 + f * 0.3); }) : null;
        var dictText = await (await fetch(BASE + s.dict)).text();
        var dict2Text = s2 ? await (await fetch(BASE + s2.dict)).text() : null;
        onProgress(1);

        var build = buildOf(opts);
        var owner = root.__ortBuild;
        var workerUrl = opts.workerUrl || DEFAULT_WORKER;
        var slug = opts.slug || root.__TOOL_SLUG || '';

        // ── may a GPU rung be tried at all ───────────────────────────────
        // The owner says whether this BROWSER should (WebKit: no); a rejection
        // remembered for this adapter, a hang earlier in this tab and a device
        // that cannot even be created each say no for THIS machine.
        var V = null, adapter = null, gpuKey = null;
        var wantGpu = build.webgpu && opts.webgpu !== false && !gpuOffHere();
        if (wantGpu) {
            V = await verifier(workerUrl);
            if (V) {
                try { adapter = await V.adapterIdentity(); } catch (_) { adapter = null; }
                var br = (String(root.navigator && navigator.userAgent).match(/(?:Edg|Chrome|Firefox|Version)\/(\d+)/) || [])[1] || '';
                gpuKey = V.verdictKey(['ppocr5', adapter ? adapter.vendor + '/' + adapter.architecture : '', adapter ? (adapter.description || adapter.device) : '', 'b' + br]);
                var rej = V.isRejected(gpuKey, 'webgpu');
                if (rej) { wantGpu = false; ae('feature_use', { slug: slug, f: 'ocr_gpu_skipped', why: 'remembered', kind: (rej.why && rej.why.kind) || '' }); }
            }
        }
        if (wantGpu && opts.webgpu !== true) wantGpu = await hasWebGpu();
        // Passed the known answer on this adapter + browser within 14 days: the
        // test is not paid again (round 1 alone is ~5 s of shader compilation; the
        // run guards below stay on). Any rejection, hang or crash drops the record.
        var trusted = !!(wantGpu && V && gpuKey && V.isAccepted && V.isAccepted(gpuKey, 'webgpu'));

        function rejectGpu(kind, v) {
            if (V && gpuKey) { try { V.remember(gpuKey, 'webgpu', { kind: kind, psnr: null, cer: v && v.cer }); } catch (_) { } }
        }
        function distrustGpu() {
            if (V && gpuKey && V.unaccept) { try { V.unaccept(gpuKey, 'webgpu'); } catch (_) { } }
        }
        function tellVerdict(v, phase) {
            if (!v || v.provider === 'wasm' || v.provider === 'cpu') return;
            var t = V && V.telemetryOf ? V.telemetryOf(v, adapter) : { ep: v.provider, ok: v.ok ? 1 : 0 };
            t.slug = slug; t.f = 'ocr_ep_verdict'; t.phase = phase;
            t.cer = v.cer != null ? v.cer : null; t.cer2 = v.cer2 != null ? v.cer2 : null; t.lines = v.lines != null ? v.lines : null;
            ae('feature_use', t);
            // Only a WRONG ANSWER is remembered (OrtEpVerify's rule): a rung that
            // threw — a lost device, an OOM — is a bad day, not a verdict.
            if (!v.ok && !v.error && !v.skipped) rejectGpu('kat', v);
        }

        // The weights went to the first worker with the message (transferred);
        // any later worker takes them from the model cache, not the network.
        async function cachedBytes() {
            return {
                det: await cachedFetch(DET.url, function () { }),
                rec: await cachedFetch(BASE + s.rec, function () { }),
                rec2: s2 ? await cachedFetch(BASE + s2.rec, function () { }) : null,
            };
        }

        /**
         * One worker, one runtime, the rungs it may climb. The build is the
         * owner's: on a browser known to need ORT 1.18 (no SIMD, or the current
         * build's shared 4 GiB memory cannot be reserved — WebKit before 18) it is
         * the legacy one, which has no threaded wasm and no WebGPU provider.
         * Init is watched in visible time: a GPU rung that never answers its
         * known-answer read must not leave the page waiting for good.
         */
        function boot(bytes, providers, b, trustedRung) {
            var w = new Worker(workerUrl);
            var verdicts = [];
            var p = new Promise(function (resolve, reject) {
                var cancel = visibleTimeout(function () {
                    off();
                    var e = new Error('worker init timeout'); e.code = 'init_timeout'; e.build = b.legacy; e.worker = w;
                    reject(e);
                }, INIT_MS);
                function off() { w.removeEventListener('message', onMsg); w.removeEventListener('error', onErr); }
                // The worker SCRIPT did not load (a 5xx on /jsv/…, an offline
                // phone) or died before it could answer: say so now, not after
                // the init budget — and it is not the GPU's fault.
                function onErr(ev) {
                    cancel(); off();
                    var e = new Error('worker_load_failed: ' + String((ev && ev.message) || 'worker error').slice(0, 160));
                    e.code = 'worker_load_failed'; e.build = b.legacy; e.worker = w;
                    reject(e);
                }
                function onMsg(ev) {
                    var m = ev.data || {};
                    if (m.type === 'verdict') { verdicts.push(m.verdict); tellVerdict(m.verdict, 'init'); return; }
                    if (m.type === 'ready') {
                        cancel(); off();
                        resolve({ worker: w, provider: m.provider, unverified: !!m.unverified, verdicts: verdicts, build: b.legacy });
                    } else if (m.type === 'error') {
                        cancel(); off();
                        var e = new Error(m.code + ': ' + m.message); e.code = m.code; e.build = b.legacy; e.worker = w;
                        reject(e);
                    }
                }
                w.addEventListener('message', onMsg);
                w.addEventListener('error', onErr);
            });
            w.postMessage({
                cmd: 'init', detBytes: bytes.det, recBytes: bytes.rec, dictText: dictText,
                rec2Bytes: bytes.rec2, dict2Text: dict2Text,
                providers: providers, webgpu: providers[0] === 'webgpu', trusted: !!trustedRung,
                threads: b.threads,
                wasmBase: b.wasmBase,
                ortUrl: b.url,
            }, bytes.rec2 ? [bytes.det, bytes.rec, bytes.rec2] : [bytes.det, bytes.rec]);
            return p;
        }
        /** A fresh worker on the CPU only — the step down from a GPU that failed. */
        async function bootCpu() {
            return boot(await cachedBytes(), ['wasm'], buildOf(opts));
        }

        var up;
        try {
            up = await boot({ det: detBytes, rec: recBytes, rec2: rec2Bytes }, wantGpu ? ['webgpu', 'wasm'] : ['wasm'], build, trusted);
            if (up.provider === 'webgpu' && !trusted && V && gpuKey && V.accept && up.verdicts.some(function (v) { return v && v.provider === 'webgpu' && v.ok; })) {
                try { V.accept(gpuKey, 'webgpu'); } catch (_) { }
            }
        } catch (e) {
            try { e.worker && e.worker.terminate(); } catch (_) { }
            // The reservation failure, on the current build: the owner marks the
            // browser and the SAME call goes again on the legacy build, in a fresh
            // worker (a failed init is cached by the runtime that failed).
            if (owner && owner.initFailed && owner.initFailed(e, { legacy: e.build || '', from: 'ocr' })) {
                build = buildOf(opts);
                up = await boot(await cachedBytes(), ['wasm'], build);
            } else if (wantGpu && e.code !== 'worker_load_failed') {
                // A GPU rung that hung or broke the runtime (ORT memoises a failed
                // initWasm, so the wasm rung in the same worker fails with it): the
                // CPU in a worker of its own.
                gpuOffForTab(e.code || 'init');
                distrustGpu();
                ae('feature_use', { slug: slug, f: 'ocr_gpu_fallback', why: e.code === 'init_timeout' ? 'init_timeout' : 'init_failed', msg: String(e.message || '').slice(0, 120) });
                up = await bootCpu();
            } else {
                throw e;
            }
        }

        var cur = { worker: up.worker, provider: up.provider };
        var seq = 0;
        // Reads in flight, by id → settle(err). close() answers them: terminate()
        // alone fires no event, so a read cut off by «Отмена» waited out the
        // silence budget (300 s on the CPU) and then reported a stall — the
        // /ocr-text-extractor button stayed dead all that time (stand 21.10.2026).
        var live = {};
        var emptyAgreed = false;      // the CPU has once confirmed an empty GPU answer
        var chain = Promise.resolve();

        /**
         * One read on one worker. Rejects with `ocr_stalled` when the worker goes
         * silent past its budget and `ocr_worker_crashed` when the worker itself
         * dies — before 14.10.2026 both left the promise pending for ever, and the
         * page on «Recognising…» (#1812: 112 s, then the person left).
         */
        function runOn(c, bitmap, o) {
            var id = ++seq;
            var lines = [];
            var gpu = c.provider !== 'wasm';
            return new Promise(function (resolve, reject) {
                var cancel = null, settled = false;
                function finish() {
                    settled = true; if (cancel) cancel();
                    delete live[id];
                    c.worker.removeEventListener('message', onMsg); c.worker.removeEventListener('error', onErr);
                }
                live[id] = function (e) { if (settled) return; finish(); reject(e); };
                function arm(ms) {
                    if (cancel) cancel();
                    cancel = visibleTimeout(function () {
                        if (settled) return;
                        finish();
                        var e = new Error('ocr_stalled: no word from the ' + c.provider + ' worker for ' + Math.round(ms / 1000) + ' s');
                        e.code = 'ocr_stalled'; e.provider = c.provider;
                        reject(e);
                    }, ms);
                }
                function onErr(ev) {
                    if (settled) return;
                    finish();
                    var e = new Error('ocr_worker_crashed: ' + String((ev && ev.message) || 'worker error').slice(0, 160));
                    e.code = 'ocr_worker_crashed'; e.provider = c.provider;
                    reject(e);
                }
                function onMsg(ev) {
                    var m = ev.data || {};
                    if (m.id !== id) return;
                    arm(gpu ? GPU_GAP_MS : CPU_GAP_MS);
                    if (m.type === 'boxes') { if (o.onBoxes) o.onBoxes(m); return; }
                    if (m.type === 'tick') return;
                    if (m.type === 'crop') { if (o.onCrop) o.onCrop(m); return; }
                    if (m.type === 'line') {
                        var text = asWritten(script, m.text);
                        var rec = { index: m.index, total: m.total, text: text, box: m.box, quad: m.quad, angle: m.angle || 0, conf: m.conf, row: m.row, words: wordsAsWritten(script, m.words) };
                        if (m.alt) rec.alt = { text: asWritten(script2, m.alt.text), conf: m.alt.conf, words: wordsAsWritten(script2, m.alt.words) };
                        lines.push(rec);
                        if (o.onLine) o.onLine(rec);
                        return;
                    }
                    if (m.type === 'done') {
                        finish();
                        resolve({ lines: lines, msDet: m.msDet, msRec: m.msRec, msTotal: m.msTotal, rows: m.rows, boxes: m.boxes, dual: !!m.dual, dualMode: m.dualMode || null, dualSolo: m.dualSolo || 0, provider: c.provider });
                        return;
                    }
                    if (m.type === 'error') {
                        finish();
                        var e = new Error(m.code + ': ' + m.message); e.code = m.code; e.provider = c.provider;
                        reject(e);
                    }
                }
                c.worker.addEventListener('message', onMsg);
                c.worker.addEventListener('error', onErr);
                arm(gpu ? GPU_FIRST_MS : CPU_GAP_MS);
                c.worker.postMessage({ cmd: 'run', id: id, bitmap: bitmap, cfg: o.cfg || {} }, [bitmap]);
            });
        }
        function switchTo(c) {
            try { cur.worker.terminate(); } catch (_) { }
            cur = c;
            api.provider = c.provider;
        }

        /**
         * A read on the GPU keeps a copy of its picture until the answer is in,
         * because three things are answered by reading it AGAIN on the CPU:
         *   · silence past the budget or a dead worker — the GPU is off for this
         *     tab and the engine lives on the CPU from here on;
         *   · an inference error on the GPU — the same;
         *   · NO LINES AT ALL — what the broken phones answered for every page.
         *     The CPU is the reference: if it finds text, the GPU read the page
         *     wrong at this size (the known answer passed at its own size) and is
         *     rejected for 30 days; if it agrees, empty answers are trusted from
         *     then on (a live camera on a blank wall must not pay this every frame).
         */
        async function readOnce(bitmap, o) {
            o = o || {};
            var spare = null;
            if (cur.provider !== 'wasm') { try { spare = await createImageBitmap(bitmap); } catch (_) { spare = null; } }
            try {
                var res;
                try {
                    res = await runOn(cur, bitmap, o);
                } catch (err) {
                    if (cur.provider === 'wasm' || !spare || !/^(ocr_stalled|ocr_worker_crashed|inference_failed)$/.test(err.code || '')) throw err;
                    gpuOffForTab(err.code);
                    distrustGpu();
                    ae('feature_use', { slug: slug, f: 'ocr_gpu_fallback', why: err.code, msg: String(err.message || '').slice(0, 120) });
                    // The stuck GPU worker goes FIRST: two engines alive measured
                    // +117 MB over one (a 4 GB phone), and this one will not answer.
                    try { cur.worker.terminate(); } catch (_) { }
                    switchTo(await bootCpu());
                    var again = spare; spare = null;
                    return await runOn(cur, again, o);
                }
                if (cur.provider !== 'wasm' && !res.lines.length && spare && !emptyAgreed) {
                    var cpu = await bootCpu();
                    var probe = spare; spare = null;
                    var ref = await runOn(cpu, probe, {});
                    if (ref.lines.length) {
                        rejectGpu('empty_vs_cpu', { cer: 1 });
                        ae('feature_use', { slug: slug, f: 'ocr_gpu_fallback', why: 'empty_vs_cpu', cpu_lines: ref.lines.length });
                        switchTo(cpu);
                        if (o.onLine) ref.lines.forEach(function (l) { o.onLine(l); });
                        return ref;
                    }
                    emptyAgreed = true;
                    try { cpu.worker.terminate(); } catch (_) { }
                }
                return res;
            } finally {
                if (spare) { try { spare.close(); } catch (_) { } }
            }
        }

        var api = {
            provider: cur.provider,
            script: script,
            script2: script2,
            lang: lang,
            unverified: up.unverified,

            /**
             * @param bitmap ImageBitmap (transferred — do not reuse it after)
             * @param o.onLine  ({index,total,text,box,quad,angle,conf,row,words}) as each line lands
             * @param o.onBoxes ({boxes,w,h}) once detection finishes
             * Reads are serialised: a recovery swaps the worker underneath, and a
             * second read must land on the worker that is current by then.
             */
            read: function (bitmap, o) {
                var task = chain.then(function () { return readOnce(bitmap, o); });
                chain = task.catch(function () { });
                return task;
            },

            close: function () {
                try { cur.worker.terminate(); } catch (_) { }
                // The reader is gone on purpose: a cancellation, not a failure —
                // __isCancelError / __isUserCancel read it as such and stay quiet.
                Object.keys(live).forEach(function (k) {
                    var e = new Error('ocr_closed: the reader was closed');
                    e.name = 'AbortError'; e.code = 'ocr_closed'; e.errorType = 'cancelled';
                    try { live[k](e); } catch (_) { }
                });
            },
        };
        return api;
    }

    root.PPOCR = {
        open: open,
        scriptFor: scriptFor,
        isCached: isCached,
        downloadMb: downloadMb,
        hasWebGpu: hasWebGpu,
        foldCyrillic: foldCyrillic,
        logicalRtl: logicalRtl,
        SCRIPTS: SCRIPTS,
    };
})(window);
